"use client";

import { NextImage } from "@/components/NextImage";

import { usePreviewSelectorScrollbar } from "./hooks/usePreviewSelectorScrollbar";
import { useScrollActiveCardIntoView } from "./hooks/useScrollActiveCardIntoView";
import { PreviewSelectorRowProps } from "./types";

import "./styles.scss";

export const PreviewSelectorRow = ({
  items,
  activeId,
  onSelect,
  className,
}: PreviewSelectorRowProps) => {
  const { scrollRef, trackRef, scrollbar, handleTrackPointerDown, handleThumbPointerDown } =
    usePreviewSelectorScrollbar({ itemsLength: items.length });

  useScrollActiveCardIntoView(scrollRef, activeId);

  const rootClassName = ["PreviewSelectorRow", className].filter(Boolean).join(" ");

  return (
    <div
      className={rootClassName}
      data-testid="preview-selector-row"
    >
      <div
        ref={scrollRef}
        className="PreviewSelectorRow__scroll"
        data-testid="preview-selector-scroll"
        tabIndex={0}
        role="region"
        aria-label="QR type selector"
      >
        <div className="PreviewSelectorRow__track">
          {items.map((item) => {
            const isActive = item.id === activeId;

            return (
              <button
                key={item.id}
                type="button"
                data-preview-card={item.id}
                className={[
                  "PreviewSelectorRow__card",
                  isActive && "PreviewSelectorRow__card--active",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-label={item.title}
                aria-selected={isActive}
                data-qa={item.data_qa}
                onClick={() => onSelect(item.id)}
              >
                <span
                  className="PreviewSelectorRow__icon"
                  aria-hidden="true"
                >
                  <NextImage
                    src={`/svg/icon-qr-${item.id}.svg`}
                    width={25}
                    height={25}
                    alt=""
                    loading="lazy"
                  />
                </span>
                <span className="PreviewSelectorRow__label">{item.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {scrollbar.visible && (
        <div
          ref={trackRef}
          className="PreviewSelectorRow__scrollbar"
          data-testid="preview-selector-scrollbar"
          onPointerDown={handleTrackPointerDown}
        >
          <div
            className="PreviewSelectorRow__scrollbar-thumb"
            data-testid="preview-selector-thumb"
            style={{
              width: `${scrollbar.thumbWidth}px`,
              transform: `translateX(${scrollbar.thumbOffset}px)`,
            }}
            role="scrollbar"
            aria-controls="preview-selector-scroll"
            aria-valuenow={scrollbar.scrollPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={-1}
            onPointerDown={handleThumbPointerDown}
          />
        </div>
      )}
    </div>
  );
};
